// src/utils/track.js
// Wrapper unificado para trackear eventos. Manda el mismo evento a los
// destinos configurados (GA4 + PostHog) en un solo llamado.
//
// Los componentes solo importan `track(...)` y no se enteran de que hay dos
// herramientas atras — si maniana se agrega Segment o Mixpanel, solo se cambia
// este archivo.
//
// Uso:
//   import { track } from '../../utils/track';
//   track('signup_completed', { method: 'google', user_type: 'individual' });

import { getPostHog } from '../components/common/PostHogProvider';

/**
 * Manda un evento a GA4 (window.gtag) y PostHog (si estan inicializados).
 *
 * @param {string} eventName  Nombre del evento (snake_case, ej: 'signup_completed')
 * @param {Object} [props]    Propiedades del evento
 */
export const track = (eventName, props = {}) => {
  // GA4
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, props);
    } catch (err) {
      // No queremos que un fallo de analytics rompa la UI
      if (process.env.NODE_ENV === 'development') {
        console.warn('[track] gtag error:', err);
      }
    }
  }

  // PostHog
  const ph = getPostHog();
  if (ph) {
    try {
      ph.capture(eventName, props);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[track] posthog error:', err);
      }
    }
  }

  // Debug helper en dev
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[track]', eventName, props);
  }
};

// ─────────────────────────────────────────────────────────────
// Atajos para eventos comunes del funnel de AVlink Network
// ─────────────────────────────────────────────────────────────

/** Click en un CTA de la landing/marketing. */
export const trackCtaClick = ({ cta, location: loc, targetPath }) => {
  track('cta_click', { cta, location: loc, target_path: targetPath });
};

/** Usuario inicia el flow de registro (llega a /register). */
export const trackSignupStarted = ({ method = 'email' } = {}) => {
  track('signup_started', { method });
};

/** Usuario completa el registro y confirma email. */
export const trackSignupCompleted = ({ method = 'email', userType = null } = {}) => {
  // sign_up es el evento "recomendado" oficial de GA4 para conversion goals.
  track('sign_up', { method, user_type: userType });
};

/** Login exitoso. */
export const trackLogin = ({ method = 'email' } = {}) => {
  track('login', { method });
};

/** El usuario completa /complete-profile (o hace skip). */
export const trackProfileCompleted = ({ userType, skipped = false }) => {
  track('profile_completed', { user_type: userType, skipped });
};

/** El usuario navega a una parte especifica del dashboard. */
export const trackDashboardSection = ({ section, userType }) => {
  track('dashboard_section_view', { section, user_type: userType });
};

/** Aplica a una oferta laboral. */
export const trackJobApply = ({ jobId, jobTitle, companyId }) => {
  track('job_apply', { job_id: jobId, job_title: jobTitle, company_id: companyId });
};

/** Manda un mensaje interno. */
export const trackMessageSent = ({ recipientType }) => {
  track('message_sent', { recipient_type: recipientType });
};
